/**
 * Campaign Plan Review Screen - Screen 3
 *
 * Execution plan review interface for autonomous campaigns.
 *
 * Design Pattern: Split-screen (left sidebar + main plan view)
 * - Left Sidebar: Campaign context, plan summary
 * - Main Area: Phase-by-phase execution plan display
 *
 * Route: /campaigns/autonomous/:session_id/plan
 */

import { ArrowLeft, CheckCircle2, Clock, Rocket, Target, Users, AlertTriangle, Edit2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function CampaignPlanReview({ previewMode = false }) {
  const { session_id } = useParams();
  const navigate = useNavigate();

  // Campaign context
  const [campaignContext, setCampaignContext] = useState({
    jobTitle: 'Senior Software Engineer',
    goal: 'Find 10 candidates with score above 70',
    refinedGoal: 'Find, evaluate, and schedule 10 qualified software engineers with evaluation score ≥70',
    status: 'ready_to_execute'
  });

  // Execution plan
  const [executionPlan, setExecutionPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  // Preview mode: Show sample plan
  const isPreview = previewMode || !session_id;

  // Sample execution plan for preview
  const samplePlan = {
    phases: [
      {
        phase: 'source',
        name: 'Sourcing',
        target_count: 50,
        agent: 'sourcing',
        tasks: [
          'Query internal database',
          'Search Apollo.io',
          'Filter by location and skills'
        ],
        estimated_minutes: 5,
        reasoning: 'Need to source more candidates than target to account for filtering and evaluation'
      },
      {
        phase: 'evaluate',
        name: 'Evaluation',
        target_count: 15,
        agent: 'evaluation',
        tasks: [
          'Score candidates (≥70)',
          'Filter qualified candidates',
          'Rank by match quality'
        ],
        estimated_minutes: 3,
        reasoning: 'Expected 30% of sourced candidates will meet the score threshold'
      },
      {
        phase: 'outreach',
        name: 'Outreach',
        target_count: 10,
        agent: 'outreach',
        tasks: [
          'Email: Primary channel',
          'WhatsApp: Fallback',
          'Personalized messaging'
        ],
        estimated_minutes: 20,
        reasoning: 'Multi-channel approach increases response rates'
      },
      {
        phase: 'schedule',
        name: 'Scheduling',
        target_count: 10,
        agent: 'scheduling',
        tasks: [
          'Schedule interviews',
          'Send calendar invites',
          'Confirm availability'
        ],
        estimated_minutes: 15,
        reasoning: 'Target 100% of contacted candidates to schedule'
      }
    ],
    reasoning: 'This plan uses a multi-phase approach to systematically source, evaluate, contact, and schedule candidates. The sourcing phase casts a wider net to ensure sufficient qualified candidates.',
    estimated_duration_days: 1,
    risks: [
      'Low candidate pool may require expanding search criteria',
      'Response rates may vary by channel',
      'Scheduling conflicts may require follow-ups'
    ]
  };

  // Load execution plan
  useEffect(() => {
    if (isPreview) {
      setExecutionPlan(samplePlan);
      return;
    }

    const loadPlan = async () => {
      setIsLoading(true);
      try {
        // Check if we have refined goal from refinement screen and update context
        const refinedGoalData = localStorage.getItem(`refined_goal_${session_id}`);
        if (refinedGoalData) {
          const refinedGoal = JSON.parse(refinedGoalData);
          setCampaignContext(prev => ({
            ...prev,
            refinedGoal: refinedGoal.conversation_summary || prev.goal,
            jobTitle: refinedGoal.job_title || prev.jobTitle,
            goal: refinedGoal.natural_language_goal || prev.goal
          }));
          localStorage.removeItem(`refined_goal_${session_id}`);
        }

        // Ask backend for an actual execution plan preview for this session
        const response = await fetch(
          `${API_BASE_URL}/api/campaigns/autonomous/${session_id}/plan`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              // Backend falls back to session form_data when this is empty
              final_form_data: {},
            }),
          }
        );

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.error('Failed to parse plan preview JSON:', text);
          throw new Error(`Invalid JSON from plan preview endpoint: ${e.message}`);
        }

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load execution plan');
        }

        // Update context from returned goal_spec if present
        if (data.goal_spec) {
          setCampaignContext(prev => ({
            ...prev,
            jobTitle: data.goal_spec.job_title || prev.jobTitle,
            goal: data.goal_spec.natural_language_goal || prev.goal,
          }));
        }

        if (data.execution_plan && Array.isArray(data.execution_plan.phases)) {
          setExecutionPlan(data.execution_plan);
        } else {
          console.warn('Execution plan missing or malformed; falling back to sample plan.');
          setExecutionPlan(samplePlan);
        }
      } catch (error) {
        console.error('Error loading execution plan:', error);
        setExecutionPlan(samplePlan);
      } finally {
        setIsLoading(false);
      }
    };

    if (session_id) {
      loadPlan();
    }
  }, [session_id, isPreview]);

  // Calculate plan summary
  const planSummary = executionPlan ? {
    totalPhases: executionPlan.phases.length,
    totalTime: executionPlan.phases.reduce((sum, phase) => sum + (phase.estimated_minutes || 0), 0),
    targetCandidates: executionPlan.phases[executionPlan.phases.length - 1]?.target_count || 0
  } : { totalPhases: 0, totalTime: 0, targetCandidates: 0 };

  // Start campaign
  const handleStartCampaign = async () => {
    if (isPreview) {
      alert('Preview mode: Campaign would start here');
      return;
    }

    setIsStarting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${session_id}/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confirmed: true,
          final_form_data: {}
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start campaign');
      }

      // Store goal_id for progress screen
      if (data.goal_id) {
        localStorage.setItem(`goal_id_${session_id}`, data.goal_id);
      }

      // Navigate to progress monitoring screen
      navigate(`/campaigns/autonomous/${session_id}/progress`);

    } catch (error) {
      console.error('Error starting campaign:', error);
      alert(`Failed to start campaign: ${error.message}`);
    } finally {
      setIsStarting(false);
    }
  };

  // Format time
  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  // Get phase icon
  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'source':
        return <Users className="w-5 h-5" />;
      case 'evaluate':
        return <Target className="w-5 h-5" />;
      case 'outreach':
        return <Rocket className="w-5 h-5" />;
      case 'schedule':
        return <CheckCircle2 className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/refine`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
              >
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Execution Plan Review</h1>
                <p className="text-sm text-gray-600 mt-1">Review and approve your campaign execution plan</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Ready to Execute
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Split Screen */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Generating execution plan...</p>
            </div>
          </div>
        ) : executionPlan ? (
          <div className="flex gap-6">
            {/* Left Sidebar - Campaign Context & Summary */}
            <div className="w-1/3 bg-white rounded-lg shadow-sm p-6 overflow-y-auto h-[calc(100vh-250px)]">
              <div className="space-y-6">
                {/* Campaign Info */}
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-500" />
                    Campaign Details
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Job Title</label>
                      <p className="text-sm text-gray-900 mt-1">{campaignContext.jobTitle}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Refined Goal</label>
                      <p className="text-sm text-gray-900 mt-1">{campaignContext.refinedGoal}</p>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-gray-600">Requirements Refined</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Plan Summary */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Plan Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Phases</span>
                      <span className="text-lg font-bold text-gray-900">{planSummary.totalPhases}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Time</span>
                      <span className="text-lg font-bold text-gray-900">{formatTime(planSummary.totalTime)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Target Candidates</span>
                      <span className="text-lg font-bold text-orange-600">{planSummary.targetCandidates}</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Duration</span>
                      <span className="font-medium text-gray-900">{executionPlan.estimated_duration_days} day(s)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sourcing Target</span>
                      <span className="font-medium text-gray-900">{executionPlan.phases[0]?.target_count || 0} candidates</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Success Metric</span>
                      <span className="font-medium text-gray-900">Scheduled Interviews</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Plan View */}
            <div className="flex-1 bg-white rounded-lg shadow-sm p-6 overflow-y-auto h-[calc(100vh-250px)]">
              <div className="space-y-6">
                {/* Plan Header */}
                <div className="border-b border-gray-200 pb-4">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Rocket className="w-6 h-6 text-orange-500" />
                    Execution Plan
                  </h2>
                  <p className="text-sm text-gray-600 mt-2">
                    ALICE has created a strategic execution plan based on your requirements
                  </p>
                </div>

                {/* Phase Cards */}
                <div className="space-y-4">
                  {executionPlan.phases.map((phase, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                            {getPhaseIcon(phase.phase)}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">
                              Phase {index + 1}: {phase.name}
                            </h3>
                            <p className="text-xs text-gray-500 capitalize">{phase.agent} agent</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">
                            Target: {phase.target_count} candidates
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(phase.estimated_minutes)}
                          </div>
                        </div>
                      </div>

                      <div className="ml-13 space-y-2">
                        <div className="text-sm text-gray-700 font-medium mb-2">Tasks:</div>
                        <ul className="space-y-1 ml-4">
                          {phase.tasks?.map((task, taskIndex) => (
                            <li key={taskIndex} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-orange-500 mt-1">•</span>
                              <span>{task}</span>
                            </li>
                          ))}
                        </ul>
                        {phase.reasoning && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 italic">{phase.reasoning}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Risk Indicators */}
                {executionPlan.risks && executionPlan.risks.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-yellow-900 mb-2">Risk Indicators</h3>
                        <ul className="space-y-1">
                          {executionPlan.risks.map((risk, index) => (
                            <li key={index} className="text-sm text-yellow-800 flex items-start gap-2">
                              <span className="text-yellow-600 mt-1">•</span>
                              <span>{risk}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/refine`)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>

                  <div className="flex gap-3">
                    <button
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
                      onClick={() => alert('Modify plan feature coming soon')}
                    >
                      <Edit2 className="w-4 h-4" />
                      Modify Plan
                    </button>
                    <button
                      onClick={handleStartCampaign}
                      disabled={isStarting}
                      className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isStarting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Starting...
                        </>
                      ) : (
                        <>
                          <Rocket className="w-4 h-4" />
                          Start Campaign
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No execution plan available</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignPlanReview;

